import dayjs from "dayjs";

import type { BCP47LangCode } from "../i18n.types";

/**
 * Locale-aware date formatting powered by the `Intl.DateTimeFormat` API.
 *
 * @param date  - Any value accepted by `new Date(…)`.
 * @param locale - BCP 47 language code (defaults to `en-US`).
 * @param options - Standard `Intl.DateTimeFormatOptions`.
 *
 * @example
 * ```ts
 * formatDate(new Date(), "en-US"); // "Feb 15, 2026"
 * formatDate(new Date(), "es-MX"); // "15 feb 2026"
 * ```
 */
export function formatDate(
  date: Date | string | number,
  locale: BCP47LangCode = "en-US",
  options?: Intl.DateTimeFormatOptions,
): string {
  const d = toSafeDate(date);
  if (!d) return "-";

  const defaultOptions: Intl.DateTimeFormatOptions = {
    year: "numeric",
    month: "short",
    day: "numeric",
    ...options,
  };

  return new Intl.DateTimeFormat(locale, defaultOptions).format(d);
}

/**
 * Locale-aware date + time formatting.
 *
 * @example
 * ```ts
 * formatDateTime(new Date(), "en-US"); // "Feb 15, 2026, 3:30 PM"
 * ```
 */
export function formatDateTime(
  date: Date | string | number,
  locale: BCP47LangCode = "en-US",
  options?: Intl.DateTimeFormatOptions,
): string {
  return formatDate(date, locale, {
    hour: "numeric",
    minute: "numeric",
    ...options,
  });
}

/**
 * Locale-aware number formatting.
 *
 * @example
 * ```ts
 * formatNumber(1234567.89, "en-US"); // "1,234,567.89"
 * formatNumber(1234567.89, "fr-CA"); // "1 234 567,89"
 * ```
 */
export function formatNumber(
  value: number,
  locale: BCP47LangCode = "en-US",
  options?: Intl.NumberFormatOptions,
): string {
  return new Intl.NumberFormat(locale, options).format(value);
}

/**
 * Locale-aware currency formatting.
 *
 * @param value    - Numeric amount.
 * @param currency - ISO 4217 currency code (e.g. `"USD"`, `"CAD"`).
 * @param locale   - BCP 47 language code.
 *
 * @example
 * ```ts
 * formatCurrency(9.99, "USD", "en-US"); // "$9.99"
 * formatCurrency(9.99, "CAD", "fr-CA"); // "9,99 $ CA"
 * ```
 */
export function formatCurrency(
  value: number,
  currency: string = "USD",
  locale: BCP47LangCode = "en-US",
  options?: Intl.NumberFormatOptions,
): string {
  return new Intl.NumberFormat(locale, {
    style: "currency",
    currency,
    ...options,
  }).format(value);
}

/**
 * Locale-aware percent formatting.
 *
 * @example
 * ```ts
 * formatPercent(0.1234, "en-US"); // "12%"
 * formatPercent(0.1234, "en-US", { maximumFractionDigits: 1 }); // "12.3%"
 * ```
 */
export function formatPercent(
  value: number,
  locale: BCP47LangCode = "en-US",
  options?: Intl.NumberFormatOptions,
): string {
  return new Intl.NumberFormat(locale, {
    style: "percent",
    maximumFractionDigits: 0,
    ...options,
  }).format(value);
}

/**
 * Relative time formatting powered by the dayjs RelativeTime plugin. Follows
 * the active dayjs locale, which the i18n layer keeps in sync with the UI
 * language (see `syncDayjsLocale`, wired into `useI18n().changeLanguage` and
 * app bootstrap), so output is localized after a language switch.
 *
 * @param date - The date to compare against now.
 *
 * @example
 * ```ts
 * formatRelativeTime(yesterday); // "a day ago" / "hace un día" / "il y a un jour"
 * ```
 */
export function formatRelativeTime(date: Date | string | number): string {
  const d = toSafeDate(date);
  if (!d) return "-";

  return dayjs(d).fromNow();
}

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

/** Safely coerce a value to a `Date`, returning `null` on failure. */
function toSafeDate(value: Date | string | number): Date | null {
  const d = value instanceof Date ? value : new Date(value);
  return isNaN(d.getTime()) ? null : d;
}
